//
// # CommandCenter
//
// Centralized command registry and keyboard shortcut dispatch for the IDE.
// Owns the list of available commands, handles keybinding matching,
// and manages the command palette open/close state.
//
// Separate from Workspace to keep concerns focused. Workspace creates
// this and passes a callback for palette state changes so the snapshot
// can update without CommandCenter needing React dependencies.
//

import type { Workspace } from "./workspace";
import {
  isTextInputFocused,
  EntityKind,
  isMac,
  hasPlatformCmdModifier,
} from "EditorCore";
import {
  scopeMatchesContext,
  type ContextScope,
  type EditorContext,
} from "./editorContext";
import {
  userPreferences,
  UserPreferencesStore,
  GridIntensity,
  IdeThemeMode,
} from "./userPreferences";

// A keyboard shortcut bound to a command.
export interface Keybinding {
  readonly key: string; // e.g. "s", "p" — the KeyboardEvent.key value
  readonly ctrl?: boolean; // Maps to Cmd on Mac, Ctrl elsewhere
  readonly shift?: boolean;
  readonly alt?: boolean;
}

// A command that can be executed from the palette or via keybinding.
export interface Command {
  readonly id: string; // Unique identifier, e.g. "file.save"
  readonly label: string; // Display name, e.g. "Save"
  readonly category?: string; // Grouping for display, e.g. "File", "View"
  readonly keybinding?: Keybinding;
  // If set, the palette prompts for a text parameter before executing.
  // The value is the placeholder text shown in the input.
  readonly parameterPrompt?: string;
  // If set, the command is only shown when this returns true.
  // Commands without this predicate are always available.
  readonly isAvailable?: () => boolean;
  // Editing contexts this command belongs to. Used by the palette to
  // prioritize contextually-relevant commands. Commands with no scopes (or
  // only "global") are relevant in every context. See editorContext.ts.
  readonly scopes?: readonly ContextScope[];
  readonly execute: (params?: string) => void | Promise<void>;
}

// A command-palette search result split into two buckets: commands the user
// can run now, and commands that exist but are unavailable in the current
// context (shown greyed-out for discoverability).
export interface CommandSearchResult {
  // Runnable commands, contextually-relevant ones first.
  readonly available: ReadonlyArray<Command>;
  // Unavailable commands (isAvailable() === false), shown greyed and
  // non-selectable. Empty when contextual mode is off.
  readonly disabled: ReadonlyArray<Command>;
}

// Whether a command is global (relevant in every context). A command with no
// scopes, or whose only scope is "global", is treated as global.
function isGlobalCommand(cmd: Command): boolean {
  const scopes = cmd.scopes;
  return !scopes || scopes.length === 0 || scopes.every((s) => s === "global");
}

// Relevance rank for ordering the runnable palette bucket. Lower is more
// relevant: context-specific match (0) < global (1) < non-matching (2).
function relevanceRank(cmd: Command, ctx: EditorContext): number {
  if (isGlobalCommand(cmd)) return 1;
  return scopeMatchesContext(cmd.scopes, ctx) ? 0 : 2;
}

// Format a keybinding for display (e.g. "Cmd+S" on Mac, "Ctrl+S" elsewhere).
export function formatKeybinding(kb: Keybinding): string {
  const parts: string[] = [];
  if (kb.ctrl) parts.push(isMac ? "Cmd" : "Ctrl");
  if (kb.alt) parts.push(isMac ? "Option" : "Alt");
  if (kb.shift) parts.push("Shift");
  parts.push(kb.key.length === 1 ? kb.key.toUpperCase() : kb.key);
  return parts.join("+");
}

// Handler for keyboard events while the palette is open.
// Set by the CommandPalette component on mount, cleared on unmount.
export type PaletteKeyHandler = (e: KeyboardEvent) => void;

// Callback that returns the active editor's key handler, or null if no
// editor is active. Used by the catch-all to route unmatched keys to
// only the focused editor tab (instead of letting the event bubble to all).
export type ActiveKeyHandler = () => ((e: KeyboardEvent) => void) | null;

export class CommandCenter {
  private _commands: Command[] = [];
  private _isPaletteOpen = false;
  private _attached = false;

  // Keyboard handler registered by the active CommandPalette component.
  // The CommandCenter delegates key events to this when the palette is open.
  private _paletteKeyHandler: PaletteKeyHandler | null = null;

  constructor(
    private readonly onPaletteStateChange: (open: boolean) => void,
    private readonly _getActiveKeyHandler: ActiveKeyHandler = () => null
  ) {}

  // Set the palette's keyboard handler. Called by CommandPalette on mount.
  setPaletteKeyHandler(handler: PaletteKeyHandler | null): void {
    this._paletteKeyHandler = handler;
  }

  // The registered command list, sorted alphabetically by label.
  get commands(): ReadonlyArray<Command> {
    return this._commands;
  }

  get isPaletteOpen(): boolean {
    return this._isPaletteOpen;
  }

  // Register a command. Replaces any existing command with the same id.
  register(cmd: Command): void {
    this.unregister(cmd.id);
    this._commands.push(cmd);
    this._commands.sort((a, b) => a.label.localeCompare(b.label));
  }

  // Remove a command by id.
  unregister(id: string): void {
    this._commands = this._commands.filter((c) => c.id !== id);
  }

  // Execute a command by id, optionally with a parameter string.
  execute(id: string, params?: string): void {
    const cmd = this._commands.find((c) => c.id === id);
    if (cmd) {
      void cmd.execute(params);
    }
  }

  // Search commands by query. Case-insensitive substring match on label and category.
  // Commands with an isAvailable predicate are excluded when it returns false.
  search(query: string): ReadonlyArray<Command> {
    const available = this._commands.filter(
      (c) => !c.isAvailable || c.isAvailable()
    );
    if (!query) return available;
    const lower = query.toLowerCase();
    return available.filter(
      (c) =>
        c.label.toLowerCase().includes(lower) ||
        (c.category && c.category.toLowerCase().includes(lower))
    );
  }

  // Context-aware search used by the command palette.
  //
  // Returns two buckets: runnable commands (contextually-relevant ones sorted
  // to the top) and unavailable commands (greyed-out, shown for
  // discoverability). Unavailable commands are only surfaced when their scope
  // matches the current context — an unavailable command that is also out of
  // context (e.g. a schematic-only command while editing a layout) is hidden
  // entirely rather than cluttering the greyed section.
  searchWithContext(query: string, ctx: EditorContext): CommandSearchResult {
    const matchesQuery = (c: Command): boolean => {
      if (!query) return true;
      const lower = query.toLowerCase();
      return (
        c.label.toLowerCase().includes(lower) ||
        (c.category?.toLowerCase().includes(lower) ?? false)
      );
    };

    const matched = this._commands.filter(matchesQuery);

    const available: Command[] = [];
    const disabled: Command[] = [];
    for (const cmd of matched) {
      const runnable = !cmd.isAvailable || cmd.isAvailable();
      if (runnable) {
        // Runnable commands stay visible regardless of context; relevance
        // ordering (below) handles in-context vs out-of-context.
        available.push(cmd);
      } else if (scopeMatchesContext(cmd.scopes, ctx)) {
        // Unavailable but in-context: show greyed for discoverability.
        disabled.push(cmd);
      }
      // Unavailable and out of context: hidden entirely.
    }

    // Order the runnable bucket by relevance so context-specific commands
    // float above generic ones:
    //   rank 0 — context-specific scope that matches the current context
    //   rank 1 — global (always-relevant) commands
    //   rank 2 — context-specific scope that does not match (rare; available
    //            outside its scope). Kept last.
    // The registry is already alphabetical and Array.sort is stable, so
    // alphabetical order is preserved within each rank.
    const ranked = available
      .map((cmd, index) => ({ cmd, index, rank: relevanceRank(cmd, ctx) }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map((r) => r.cmd);

    return { available: ranked, disabled };
  }

  // Open the command palette.
  openPalette(): void {
    if (this._isPaletteOpen) return;
    this._isPaletteOpen = true;
    this.onPaletteStateChange(true);
  }

  // Close the command palette.
  closePalette(): void {
    if (!this._isPaletteOpen) return;
    this._isPaletteOpen = false;
    this.onPaletteStateChange(false);
  }

  // Attach the keyboard listener to the window (capture phase).
  // Call once when the IDE mounts.
  attach(): void {
    if (this._attached) return;
    this._attached = true;
    window.addEventListener("keydown", this.handleKeyDown, true);
  }

  // Detach the keyboard listener.
  detach(): void {
    if (!this._attached) return;
    this._attached = false;
    window.removeEventListener("keydown", this.handleKeyDown, true);
  }

  // The centralized keyboard handler. Runs in capture phase so it fires
  // before EditorCore's bubble-phase listener.
  private handleKeyDown = (e: KeyboardEvent): void => {
    // When the palette is open, block all keystrokes from reaching
    // EditorCore and delegate to the palette's own key handler.
    if (this._isPaletteOpen) {
      // Let the browser handle Tab for accessibility within the palette
      if (e.key === "Tab") return;
      e.stopImmediatePropagation();
      // Only preventDefault for navigation keys — let character keys
      // reach the input's default behavior (typing text).
      if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "Enter" ||
        e.key === "Escape"
      ) {
        e.preventDefault();
      }
      // Delegate to the CommandPalette component's key handler
      if (this._paletteKeyHandler) {
        this._paletteKeyHandler(e);
      }
      return;
    }

    // Check for Ctrl/Cmd+P to open the palette
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key === "p") {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.openPalette();
      return;
    }

    // If a text input has focus, bail out so the event propagates
    // normally through the DOM. This lets React onKeyDown handlers
    // fire (e.g. Enter to send in AI chat, Enter to confirm in
    // dialogs). EditorCore's handleKey has a matching guard to
    // ignore keys when a text input is focused.
    if (isTextInputFocused()) {
      return;
    }

    // Check registered keybindings
    for (const cmd of this._commands) {
      if (!cmd.keybinding) continue;
      if (this.matchesKeybinding(e, cmd.keybinding)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        void cmd.execute();
        return;
      }
    }

    // Catch-all: route unmatched keys to only the active editor.
    // Without this, every EditorCore instance's bubble-phase keydown
    // listener fires, causing actions in ALL open tabs instead of
    // just the focused one.
    const handler = this._getActiveKeyHandler();
    if (handler) {
      e.stopImmediatePropagation();
      handler(e);
    }
  };

  // Check whether a KeyboardEvent matches a Keybinding.
  private matchesKeybinding(e: KeyboardEvent, kb: Keybinding): boolean {
    const ctrlOrCmd = hasPlatformCmdModifier(e);
    if ((kb.ctrl ?? false) !== ctrlOrCmd) return false;
    if ((kb.shift ?? false) !== e.shiftKey) return false;
    if ((kb.alt ?? false) !== e.altKey) return false;
    // On Mac, if ctrl is required, check metaKey; ensure we don't
    // false-match when the other modifier is also pressed
    if (kb.ctrl && isMac && e.ctrlKey) return false;
    if (!kb.ctrl && ctrlOrCmd) return false;
    // On Mac, Option (Alt) changes e.key to special characters (e.g. Option+R → "®").
    // Use e.code ("KeyR" → "r") for alt combos to get the physical key regardless.
    const eventKey =
      e.altKey && e.code.startsWith("Key")
        ? e.code.slice(3).toLowerCase()
        : e.key.toLowerCase();
    return eventKey === kb.key.toLowerCase();
  }
}

// Register the built-in commands that ship with the IDE.
export function registerBuiltinCommands(
  cc: CommandCenter,
  workspace: Workspace
): void {
  cc.register({
    id: "file.save",
    label: "Save",
    category: "File",
    keybinding: { key: "s", ctrl: true },
    execute: () => {
      void workspace.save();
    },
  });

  cc.register({
    id: "file.saveAs",
    label: "Save As...",
    category: "File",
    isAvailable: () => workspace.getActiveEditor() !== null,
    execute: () => {
      workspace.openSaveAsDialog();
    },
  });

  cc.register({
    id: "file.newDesign",
    label: "New Design...",
    category: "File",
    execute: () => {
      workspace.openCreateDesignDialog();
    },
  });

  cc.register({
    id: "schematic.generateSymbol",
    label: "Generate Symbol",
    category: "Schematic",
    scopes: ["schematic"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return false;
      const content = editor.content;
      return content.contentKind === "schematic" && !content.symbolImpl;
    },
    execute: () => {
      workspace.openGenSymbolFromSchDialog();
    },
  });

  cc.register({
    id: "design.manageLinkages",
    label: "Manage Linkages...",
    category: "Design",
    scopes: ["schematic", "symbol", "layout"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return false;
      return editor.content.contentKind !== "graphics";
    },
    execute: () => {
      workspace.openManageLinkagesDialog();
    },
  });

  cc.register({
    id: "edit.addInstance",
    label: "Add Instance...",
    category: "Edit",
    scopes: ["schematic", "layout"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      // Add Instance is available for schematics and layouts, not symbols
      return (
        editor.content.contentKind === "schematic" ||
        editor.content.contentKind === "layout"
      );
    },
    execute: () => {
      const snapshot = workspace.getSnapshot();
      if (!snapshot.activeIdent) return;
      // Compute the center of the current viewport in world coordinates
      // so the new instance is placed where the user is looking.
      const editor = workspace.getActiveEditor();
      let position = { x: 0, y: 0 };
      if (editor) {
        const { width, height } = editor.canvas.size;
        const center = editor.canvas.worldTransforms.toSch({
          x: width / 2,
          y: height / 2,
        });
        position = { x: center.x, y: center.y };
      }
      workspace.openAddInstanceDialog(snapshot.activeIdent, position);
    },
  });

  cc.register({
    id: "layout.setOutlineToBbox",
    label: "Set Outline to Bounding Box",
    category: "Layout",
    scopes: ["layout"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "layout";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      if (editor.content.contentKind !== "layout") return;
      editor.content.setOutlineToBbox();
    },
  });

  cc.register({
    id: "schematic.addPin",
    label: "Add Pin",
    category: "Schematic",
    scopes: ["schematic"],
    keybinding: { key: "p" },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "schematic";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      if (editor.content.contentKind !== "schematic") return;
      // Enter AddSchPin mode directly via the schematic's method.
      editor.content.enterAddPinMode();
    },
  });

  cc.register({
    id: "schematic.drawWire",
    label: "Draw Wire",
    category: "Schematic",
    scopes: ["schematic"],
    keybinding: { key: "w" },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "schematic";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      if (editor.content.contentKind !== "schematic") return;
      // Enter DrawWire mode directly via the schematic's method.
      editor.content.enterDrawWireMode();
    },
  });

  cc.register({
    id: "schematic.openSrvPanel",
    label: "Open Check & Save Panel",
    category: "Verification",
    keybinding: { key: "v", ctrl: true, shift: true },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return false;
      return editor.content.contentKind === "schematic";
    },
    execute: () => {
      // Load the rule list / mark the panel visible in the model, then surface
      // the Dockview panel (registered in WorkspaceLayout).
      workspace.srv.openPanel();
      workspace.showPanel("srv");
    },
  });

  cc.register({
    id: "schematic.runSrv",
    label: "Check & Save",
    category: "Verification",
    // Check & Save runs the schematic checks and saves. Cmd/Ctrl+Enter is an
    // "execute/commit" chord that (unlike Cmd/Ctrl+Shift+S) is neither reserved
    // by the browser nor commonly claimed by OS-level screenshot/clipboard
    // tools, so the keydown reliably reaches the app. `runSrv` guards on an
    // active schematic and toasts otherwise, so it is safe to fire even though
    // keybinding dispatch does not consult `isAvailable`.
    keybinding: { key: "Enter", ctrl: true },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return false;
      return editor.content.contentKind === "schematic";
    },
    execute: () => {
      void workspace.srv.runSrv();
    },
  });

  cc.register({
    id: "schematic.editWireLabel",
    label: "Edit Wire Label",
    category: "Schematic",
    scopes: ["schematic"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      const content = editor.content;
      if (content.contentKind !== "schematic") return false;
      // Check if exactly one wire is selected
      if (content.selection.size !== 1) return false;
      const selected = Array.from(content.selection)[0];
      return selected.entityKind === EntityKind.WireSegment;
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      const content = editor.content;
      if (content.contentKind !== "schematic") return;
      const selected = Array.from(content.selection)[0];
      if (selected.entityKind !== EntityKind.WireSegment) return;
      // Open the edit wire label dialog
      editor.wireContextMenu.openEditDialog(selected);
    },
  });

  // -- View commands --
  // These correspond to the top-level shortcuts in EditorCore's handleKey
  // (above the isLocked guard). Registering them here with keybindings
  // ensures they only fire in the active editor tab, not all open tabs.

  cc.register({
    id: "schematic.navigateToSymbol",
    label: "Go to Symbol",
    category: "Schematic",
    scopes: ["schematic"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return false;
      const content = editor.content;
      return content.contentKind === "schematic" && !!content.symbolImpl;
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return;
      const content = editor.content;
      if (content.contentKind !== "schematic" || !content.symbolImpl) return;
      void workspace.handleOpenRequest(content.symbolImpl);
    },
  });

  cc.register({
    id: "symbol.navigateToSchematic",
    label: "Go to Schematic",
    category: "Symbol",
    scopes: ["symbol"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return false;
      const content = editor.content;
      return (
        content.contentKind === "symbol" &&
        !!content.render.schematicImpls &&
        content.render.schematicImpls.length > 0
      );
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return;
      const content = editor.content;
      if (
        content.contentKind !== "symbol" ||
        !content.render.schematicImpls ||
        content.render.schematicImpls.length === 0
      )
        return;

      if (content.render.schematicImpls.length === 1) {
        // Single schematic: navigate directly
        void workspace.handleOpenRequest(
          content.render.schematicImpls[0].target
        );
      } else {
        // Multiple schematics: open picker dialog, filtered by symbol's pdkId
        workspace.openSchematicPickerDialog(
          content.render.schematicImpls,
          content.render.pdkId
        );
      }
    },
  });

  cc.register({
    id: "schematic.navigateToLayout",
    label: "Go to Layout",
    category: "Schematic",
    scopes: ["schematic"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return false;
      const content = editor.content;
      return (
        content.contentKind === "schematic" &&
        !!content.layoutImpls &&
        content.layoutImpls.length > 0
      );
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return;
      const content = editor.content;
      if (
        content.contentKind !== "schematic" ||
        !content.layoutImpls ||
        content.layoutImpls.length === 0
      )
        return;

      if (content.layoutImpls.length === 1) {
        // Single layout: navigate directly
        void workspace.handleOpenRequest(content.layoutImpls[0].target);
      } else {
        // Multiple layouts: open picker dialog, filtered by schematic's pdkId
        workspace.openLayoutPickerDialog(content.layoutImpls, content.pdkId);
      }
    },
  });

  cc.register({
    id: "layout.navigateToSchematic",
    label: "Go to Schematic",
    category: "Layout",
    scopes: ["layout"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return false;
      const content = editor.content;
      return content.contentKind === "layout" && !!content.schematicImpl;
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor) return;
      const content = editor.content;
      if (content.contentKind !== "layout" || !content.schematicImpl) return;
      void workspace.handleOpenRequest(content.schematicImpl);
    },
  });

  // -- Design-level commands --
  // These operate on the whole active design (not a clicked entity), mirroring
  // the generic items in the empty-space right-click menu.

  // Reveal the current design in the Depot tree. OpenAccess (libcellview)
  // designs live in the OpenAccess tree instead, so they get the sibling
  // "View in OpenAccess" command below — only one is available at a time,
  // keeping the palette label accurate for the active design.
  cc.register({
    id: "design.viewInDepot",
    label: "View in Depot",
    category: "Design",
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.getIdent().kind !== "libcellview";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) editor.platform.notifyViewCurrentDesignInDepot();
    },
  });

  cc.register({
    id: "design.viewInOpenAccess",
    label: "View in OpenAccess",
    category: "Design",
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.getIdent().kind === "libcellview";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) editor.platform.notifyViewCurrentDesignInDepot();
    },
  });

  // Navigate back to the design this one was opened from (the breadcrumb parent).
  cc.register({
    id: "design.openParent",
    label: "Open Parent",
    category: "Design",
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.platform.hasParentBreadcrumb();
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) editor.platform.notifyOpenParent();
    },
  });

  cc.register({
    id: "layout.configurePowerRouting",
    label: "Configure Power Routing...",
    category: "Layout",
    scopes: ["layout"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "layout";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor && editor.content.contentKind === "layout") {
        editor.platform.notifyConfigurePowerRouting();
      }
    },
  });

  cc.register({
    id: "view.trackpadMode",
    label: "Toggle Trackpad Mode",
    category: "View",
    keybinding: { key: "T", shift: true },
    isAvailable: () => !!workspace.getActiveEditor(),
    execute: () => {
      const next = !userPreferences.get().trackpadMode;
      userPreferences.setTrackpadMode(next);
      const editor = workspace.getActiveEditor();
      if (editor) {
        editor.showFeedback(`Trackpad mode: ${next ? "ON" : "OFF"}`);
      }
    },
  });

  cc.register({
    id: "view.toggleGrid",
    label: "Toggle Grid",
    category: "View",
    keybinding: { key: "G", shift: true },
    isAvailable: () => !!workspace.getActiveEditor(),
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) editor.grid.toggle();
    },
  });

  cc.register({
    id: "view.toggleOutlines",
    label: "Toggle Outlines",
    category: "View",
    keybinding: { key: "O", shift: true },
    isAvailable: () => !!workspace.getActiveEditor(),
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) {
        editor.showOutlines = !editor.showOutlines;
        if (editor.showOutlines && "initOutline" in editor.content) {
          (editor.content as { initOutline: () => void }).initOutline();
        }
        editor.requestRedraw();
      }
    },
  });

  cc.register({
    id: "layout.drawRoute",
    label: "Toggle Draw Route Mode",
    category: "Layout",
    scopes: ["layout"],
    keybinding: { key: "r", alt: true },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "layout";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) {
        editor.toggleDrawRoute();
      }
    },
  });

  cc.register({
    id: "view.toggleTheme",
    label: "Toggle Theme (Light / Dark / Auto)",
    category: "View",
    keybinding: { key: "t" },
    isAvailable: () => true,
    execute: () => {
      const current = userPreferences.get().themeMode;
      const next = UserPreferencesStore.nextThemeMode(current);
      userPreferences.setThemeMode(next);
      const editor = workspace.getActiveEditor();
      if (editor) {
        const label =
          next === IdeThemeMode.Auto
            ? `Theme: Auto (${userPreferences.resolvedTheme()})`
            : `Theme: ${next.charAt(0).toUpperCase() + next.slice(1)}`;
        editor.showFeedback(label);
      }
    },
  });

  cc.register({
    id: "view.toggleGridIntensity",
    label: "Toggle Grid Intensity",
    category: "View",
    isAvailable: () => !!workspace.getActiveEditor(),
    execute: () => {
      const current = userPreferences.get().gridIntensity;
      const next = GridIntensity.other(current);
      userPreferences.setGridIntensity(next);
      const label =
        next === GridIntensity.High
          ? "Grid: High Intensity"
          : "Grid: Normal Intensity";
      const editor = workspace.getActiveEditor();
      if (editor) editor.showFeedback(label);
    },
  });

  cc.register({
    id: "view.redraw",
    label: "Redraw",
    category: "View",
    keybinding: { key: "R", shift: true },
    isAvailable: () => !!workspace.getActiveEditor(),
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) editor.requestRedraw();
    },
  });

  cc.register({
    id: "view.zoomToFit",
    label: "Zoom to Fit",
    category: "View",
    keybinding: { key: "f" },
    isAvailable: () => !!workspace.getActiveEditor(),
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) {
        const hasSelection = editor.content.selectionBbox() !== null;
        editor.fit();
        editor.showFeedback(
          hasSelection ? "Zoomed to fit selection" : "Zoomed to fit design"
        );
      }
    },
  });

  cc.register({
    id: "view.toggleCounters",
    label: "Toggle Counters (debug information)",
    category: "View",
    isAvailable: () => !!workspace.getActiveEditor(),
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) editor.uiState.counters.toggle();
    },
  });

  cc.register({
    id: "view.clearCounters",
    label: "Clear Counters (debug information)",
    category: "View",
    isAvailable: () => !!workspace.getActiveEditor(),
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor) editor.uiState.counters.clear();
    },
  });

  cc.register({
    id: "view.toggleLayerInfo",
    label: "Show Layers",
    category: "View",
    scopes: ["layout"],
    keybinding: { key: "l" },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "layout";
    },
    execute: () => {
      workspace.focusLayerPanel();
    },
  });

  cc.register({
    id: "view.showAllLayers",
    label: "Show All Layers",
    category: "View",
    scopes: ["layout"],
    keybinding: { key: "a" },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "layout";
    },
    execute: () => {
      workspace.showAllLayers();
    },
  });

  cc.register({
    id: "view.keyboardHelp",
    label: "Keyboard Shortcuts",
    category: "View",
    keybinding: { key: "?", shift: true },
    isAvailable: () => !!workspace.getActiveEditor(),
    execute: () => {
      workspace.focusKeyboardHelpPanel();
    },
  });

  cc.register({
    id: "help.showHelp",
    label: "Help",
    category: "Help",
    execute: () => {
      workspace.showPanel("help");
    },
  });

  cc.register({
    id: "help.reportIssue",
    label: "Report an Issue…",
    category: "Help",
    execute: () => {
      workspace.openReportIssueDialog();
    },
  });

  cc.register({
    id: "view.togglePinLabels",
    label: "Toggle Pin Label Visibility",
    category: "View",
    scopes: ["layout"],
    keybinding: { key: "4" },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "layout";
    },
    execute: () => {
      workspace.toggleDisplayOption("pinLabels");
    },
  });

  cc.register({
    id: "view.toggleInstanceLabels",
    label: "Toggle Instance Label Visibility",
    category: "View",
    scopes: ["layout"],
    keybinding: { key: "5" },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "layout";
    },
    execute: () => {
      workspace.toggleDisplayOption("instanceLabels");
    },
  });

  cc.register({
    id: "view.toggleRouteLabels",
    label: "Toggle Route Label Visibility",
    category: "View",
    scopes: ["layout"],
    keybinding: { key: "6" },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "layout";
    },
    execute: () => {
      workspace.toggleDisplayOption("routeLabels");
    },
  });

  cc.register({
    id: "view.togglePins",
    label: "Toggle Pin Visibility",
    category: "View",
    scopes: ["layout"],
    keybinding: { key: "7" },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "layout";
    },
    execute: () => {
      workspace.toggleDisplayOption("pins");
    },
  });

  cc.register({
    id: "view.toggleAbstractView",
    label: "Toggle Abstract View",
    category: "View",
    scopes: ["layout"],
    keybinding: { key: "8" },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "layout";
    },
    execute: () => {
      workspace.toggleDisplayOption("abstractView");
    },
  });

  cc.register({
    id: "view.toggleBlockages",
    label: "Toggle Blockage Visibility",
    category: "View",
    scopes: ["layout"],
    keybinding: { key: "B", shift: true },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "layout";
    },
    execute: () => {
      workspace.toggleDisplayOption("blockages");
    },
  });

  // -- Edit commands --
  // These are below EditorCore's isLocked guard, so we replicate that check.

  cc.register({
    id: "edit.undo",
    label: "Undo",
    category: "Edit",
    keybinding: { key: "z", ctrl: true },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && !editor.isLocked;
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor && !editor.isLocked) {
        const result = editor.content.undo();
        if (result) editor.showFeedback("Undo");
      }
    },
  });

  cc.register({
    id: "edit.redo",
    label: "Redo",
    category: "Edit",
    keybinding: { key: "z", ctrl: true, shift: true },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && !editor.isLocked;
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor && !editor.isLocked) {
        const result = editor.content.redo();
        if (result) editor.showFeedback("Redo");
      }
    },
  });

  cc.register({
    id: "edit.selectAll",
    label: "Select All",
    category: "Edit",
    scopes: ["schematic", "layout"],
    keybinding: { key: "a", ctrl: true },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      // Select-all is meaningful in schematics and layouts, not graphics/symbols.
      return (
        editor.content.contentKind === "schematic" ||
        editor.content.contentKind === "layout"
      );
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor && !editor.isLocked) editor.selectAll();
    },
  });

  // -- Tools commands --

  cc.register({
    id: "tools.runPex",
    label: "Run Parasitics Extraction (PEX)",
    category: "Tools",
    scopes: ["layout"],
    keybinding: { key: "e", ctrl: true, shift: true },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "layout";
    },
    execute: () => {
      void workspace.runPex();
    },
  });

  cc.register({
    id: "tools.generateFromSource",
    label: "Layout: Generate From Source",
    category: "Tools",
    scopes: ["schematic"],
    keybinding: { key: "l", ctrl: true, shift: true },
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "schematic";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (editor && !editor.isLocked) {
        editor.platform.notifyGenerateFromSource();
      }
    },
  });

  // -- Symbol graphics drawing --
  // Enter the interactive draw modes for the symbol editor's shape primitives.
  // These mirror the keyboard shortcuts (handled in EditorCore's symbol
  // handleKey) and the right-click "Draw …" context-menu items. No keybinding
  // is attached here: the palette-level keydown handler matches keybindings
  // globally (regardless of scope), so a plain-letter binding would be
  // swallowed in schematic/layout too. The shortcuts live in EditorCore where
  // they fire only for the active symbol editor.

  cc.register({
    id: "symbol.drawRectangle",
    label: "Draw Rectangle",
    category: "Symbol",
    scopes: ["symbol"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "symbol";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      if (editor.content.contentKind !== "symbol") return;
      editor.content.annotations.startAddRect();
    },
  });

  cc.register({
    id: "schematic.addText",
    label: "Add Text Annotation",
    category: "Schematic",
    scopes: ["schematic"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "schematic";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      if (editor.content.contentKind !== "schematic") return;
      editor.content.annotations.startAddText();
    },
  });

  cc.register({
    id: "symbol.drawPolygon",
    label: "Draw Polygon",
    category: "Symbol",
    scopes: ["symbol"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "symbol";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      if (editor.content.contentKind !== "symbol") return;
      editor.content.annotations.startAddPolygon();
    },
  });

  cc.register({
    id: "symbol.drawPath",
    label: "Draw Path",
    category: "Symbol",
    scopes: ["symbol"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "symbol";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      if (editor.content.contentKind !== "symbol") return;
      editor.content.annotations.startAddPath();
    },
  });

  cc.register({
    id: "symbol.drawEllipse",
    label: "Draw Ellipse",
    category: "Symbol",
    scopes: ["symbol"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "symbol";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      if (editor.content.contentKind !== "symbol") return;
      editor.content.annotations.startAddEllipse();
    },
  });

  cc.register({
    id: "symbol.drawArc",
    label: "Draw Arc",
    category: "Symbol",
    scopes: ["symbol"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return false;
      return editor.content.contentKind === "symbol";
    },
    execute: () => {
      const editor = workspace.getActiveEditor();
      if (!editor || editor.isLocked) return;
      if (editor.content.contentKind !== "symbol") return;
      editor.content.annotations.startAddArc();
    },
  });

  cc.register({
    id: "symbol.graphicsCatalogue",
    label: "Add Graphics from Catalog",
    category: "Symbol",
    scopes: ["symbol"],
    isAvailable: () => {
      const editor = workspace.getActiveEditor();
      return !!editor && editor.content.contentKind === "symbol";
    },
    execute: () => {
      workspace.openGraphicsCatalogueDialog();
    },
  });
}
